import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  ArrowsClockwise, 
  Plus,
  CalendarX,
  Trash,
  X
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const AdminRecurring = () => {
  const { t, language, API } = useAuth();
  const dateLocale = language === 'de' ? de : enUS;
  
  const [recurring, setRecurring] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [selectedRecurring, setSelectedRecurring] = useState(null);
  const [skipDate, setSkipDate] = useState(null);
  
  // Customer search
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [custSearching, setCustSearching] = useState(false);
  const [selectedCustName, setSelectedCustName] = useState('');
  const [showCustDropdown, setShowCustDropdown] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [multiCourt, setMultiCourt] = useState(false);
  const [selectedCourts, setSelectedCourts] = useState([]);
  
  const [formData, setFormData] = useState({
    court_id: '',
    start_date: '',
    end_date: '',
    weekday: 0,
    start_time: '19:00',
    duration_minutes: 90,
    customer_id: '',
    notes: ''
  });

  const weekdays = language === 'de' 
    ? ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag']
    : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recurringRes, courtsRes] = await Promise.all([
        API.get('/recurring-bookings'),
        API.get('/courts')
      ]);
      setRecurring(recurringRes.data);
      setCourts(courtsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced customer search
  useEffect(() => {
    if (custSearch.length < 2) { setCustResults([]); return; }
    const timer = setTimeout(async () => {
      setCustSearching(true);
      try {
        const { data } = await API.get(`/users/search?q=${encodeURIComponent(custSearch)}&limit=20`);
        setCustResults(data);
        setShowCustDropdown(true);
      } catch (e) { console.error(e); }
      finally { setCustSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [custSearch]);


  const handleSubmit = async () => {
    const courtsToBook = multiCourt ? selectedCourts : [formData.court_id];
    if (courtsToBook.length === 0 || !courtsToBook[0]) {
      toast.error(language === 'de' ? 'Bitte mindestens einen Platz wählen' : 'Please select at least one court');
      return;
    }
    try {
      let successCount = 0;
      let failCount = 0;
      let conflictInfo = [];
      for (const courtId of courtsToBook) {
        try {
          await API.post('/recurring-bookings', {
            ...formData,
            court_id: courtId,
            weekday: parseInt(formData.weekday),
            duration_minutes: parseInt(formData.duration_minutes),
            customer_id: formData.customer_id || null
          });
          successCount++;
        } catch (error) {
          failCount++;
          const detail = error.response?.data?.detail;
          const courtName = courts.find(c => c.id === courtId)?.name || courtId;
          if (detail && typeof detail === 'object' && detail.conflict_dates) {
            conflictInfo.push(`${courtName}: ${detail.conflict_dates.join(', ')}`);
          }
        }
      }
      if (successCount > 0) {
        const msg = courtsToBook.length > 1
          ? `${successCount} ${language === 'de' ? 'wiederkehrende Buchungen erstellt' : 'recurring bookings created'}${failCount > 0 ? ` (${failCount} ${language === 'de' ? 'fehlgeschlagen' : 'failed'})` : ''}`
          : (language === 'de' ? 'Wiederkehrende Buchung erstellt!' : 'Recurring booking created!');
        toast.success(msg);
        setShowModal(false);
        resetForm();
        loadData();
      }
      if (conflictInfo.length > 0) {
        toast.error(`${language === 'de' ? 'Konflikte' : 'Conflicts'}: ${conflictInfo.join(' | ')}`);
      }
      if (successCount === 0 && conflictInfo.length === 0) {
        toast.error(language === 'de' ? 'Fehler' : 'Error');
      }
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler' : 'Error');
    }
  };

  const handleSkipDate = async () => {
    if (!selectedRecurring || !skipDate) return;
    
    try {
      const dateStr = format(skipDate, 'yyyy-MM-dd');
      await API.post(`/recurring-bookings/${selectedRecurring.id}/skip`, { date: dateStr });
      toast.success(language === 'de' ? 'Datum übersprungen' : 'Date skipped');
      setShowSkipModal(false);
      setSkipDate(null);
      loadData();
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler' : 'Error');
    }
  };

  const handleDelete = async (recurringId) => {
    setConfirmAction({
      message: language === 'de' ? 'Wirklich löschen? Zukünftige Buchungen werden storniert.' : 'Delete? Future bookings will be cancelled.',
      onConfirm: async () => {
        try {
          await API.delete(`/recurring-bookings/${recurringId}?delete_future=true`);
          toast.success(language === 'de' ? 'Gelöscht' : 'Deleted');
          loadData();
        } catch (error) {
          toast.error(language === 'de' ? 'Fehler' : 'Error');
        }
        setConfirmAction(null);
      }
    });
  };

  const resetForm = () => {
    setFormData({
      court_id: '',
      start_date: '',
      end_date: '',
      weekday: 0,
      start_time: '19:00',
      duration_minutes: 90,
      customer_id: '',
      notes: ''
    });
    setMultiCourt(false);
    setSelectedCourts([]);
    setCustSearch('');
    setSelectedCustName('');
    setCustResults([]);
    setShowCustDropdown(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-recurring">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            {t('recurring')}
          </h1>
          <p className="text-slate-500">
            {language === 'de' 
              ? 'Wiederkehrende Buchungen verwalten'
              : 'Manage recurring bookings'}
          </p>
        </div>
        
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="create-recurring-btn"
        >
          <Plus size={18} weight="bold" />
          {language === 'de' ? 'Neue Serie' : 'New Series'}
        </Button>
      </div>

      {/* Recurring Bookings */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-lg" />
          ))}
        </div>
      ) : recurring.length === 0 ? (
        <div className="card-static p-12 text-center">
          <ArrowsClockwise size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
          <p className="text-slate-500 mb-4">
            {language === 'de' ? 'Keine wiederkehrenden Buchungen' : 'No recurring bookings'}
          </p>
          <Button onClick={() => setShowModal(true)} className="btn-primary">
            {language === 'de' ? 'Erste Serie erstellen' : 'Create First Series'}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {recurring.map((r) => (
            <div
              key={r.id}
              className="card-static p-6"
              data-testid={`recurring-card-${r.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="font-bold text-slate-900 text-lg">{r.court_name}</h3>
                    <span className="px-2 py-1 text-xs font-bold uppercase rounded-lg bg-blue-50 text-blue-600">
                      {weekdays[r.weekday]}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">{language === 'de' ? 'Zeit' : 'Time'}</p>
                      <p className="text-slate-900">{r.start_time} ({r.duration_minutes} min)</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{language === 'de' ? 'Zeitraum' : 'Period'}</p>
                      <p className="text-slate-900">
                        {format(new Date(r.start_date), 'PP', { locale: dateLocale })} - {format(new Date(r.end_date), 'PP', { locale: dateLocale })}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">{language === 'de' ? 'Kunde' : 'Customer'}</p>
                      <p className="text-slate-900">{r.customer_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{language === 'de' ? 'Übersprungen' : 'Skipped'}</p>
                      <p className="text-amber-600">{r.skip_dates?.length || 0} {language === 'de' ? 'Termine' : 'dates'}</p>
                    </div>
                  </div>
                  {r.notes && <p className="text-slate-500 text-sm mt-2 italic">"{r.notes}"</p>}
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => { setSelectedRecurring(r); setShowSkipModal(true); }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <CalendarX size={16} />
                    {language === 'de' ? 'Überspringen' : 'Skip Date'}
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={() => handleDelete(r.id)}
                    className="text-red-500 hover:bg-red-50"
                    data-testid={`delete-recurring-${r.id}`}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Recurring Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recurring-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {language === 'de' ? 'Neue wiederkehrende Buchung' : 'New Recurring Booking'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {language === 'de' 
                ? 'z.B. jeden Montag von 19:00-20:30'
                : 'e.g. every Monday from 19:00-20:30'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Court Selection */}
            <div className="space-y-3">
              <Label className="label-text">{t('courts')} *</Label>
              
              {/* Single court select (when multi is OFF) */}
              {!multiCourt && (
                <Select value={formData.court_id} onValueChange={(v) => { setFormData({...formData, court_id: v}); setSelectedCourts([v]); }}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900" data-testid="recurring-court-select">
                    <SelectValue placeholder={language === 'de' ? 'Platz wählen' : 'Select court'} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 z-[100]">
                    {courts.map(court => (
                      <SelectItem key={court.id} value={court.id}>{court.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Multi-court toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const next = !multiCourt;
                    setMultiCourt(next);
                    if (next) {
                      setSelectedCourts(formData.court_id ? [formData.court_id] : []);
                    } else {
                      setSelectedCourts(selectedCourts.length > 0 ? [selectedCourts[0]] : []);
                      setFormData({...formData, court_id: selectedCourts[0] || ''});
                    }
                  }}
                  className={`relative w-11 h-6 rounded-full transition-colors ${multiCourt ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  data-testid="recurring-multi-court-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${multiCourt ? 'translate-x-5' : ''}`} />
                </button>
                <span className="text-sm font-medium text-slate-700">
                  {language === 'de' ? 'Mehrere Plätze' : 'Multiple Courts'}
                  {multiCourt && selectedCourts.length > 0 && <span className="text-emerald-600 ml-1">({selectedCourts.length})</span>}
                </span>
              </div>

              {/* Court grid (when multi is ON) */}
              {multiCourt && (
                <div className="grid grid-cols-4 gap-2" data-testid="recurring-court-grid">
                  {courts.map(court => {
                    const selected = selectedCourts.includes(court.id);
                    return (
                      <button key={court.id}
                        onClick={() => setSelectedCourts(prev => prev.includes(court.id) ? prev.filter(id => id !== court.id) : [...prev, court.id])}
                        className={`p-2.5 rounded-xl text-xs font-bold border-2 transition-all ${
                          selected ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
                        }`}
                        data-testid={`recurring-court-${court.id}`}
                      >
                        {court.name}
                      </button>
                    );
                  })}
                  <button onClick={() => setSelectedCourts(courts.map(c => c.id))}
                    className="col-span-4 p-1.5 rounded-lg text-xs font-medium border border-dashed border-emerald-300 text-emerald-600 hover:bg-emerald-50 transition-all"
                    data-testid="recurring-select-all">
                    {language === 'de' ? 'Alle auswählen' : 'Select All'}
                  </button>
                </div>
              )}
            </div>

            {/* Weekday */}
            <div className="space-y-2">
              <Label className="label-text">{language === 'de' ? 'Wochentag' : 'Weekday'} *</Label>
              <Select value={formData.weekday.toString()} onValueChange={(v) => setFormData({...formData, weekday: parseInt(v)})}>
                <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 z-[100]">
                  {weekdays.map((day, index) => (
                    <SelectItem key={index} value={index.toString()}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Startzeit' : 'Start Time'} *</Label>
                <Select value={formData.start_time} onValueChange={(v) => setFormData({...formData, start_time: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 z-[100] max-h-48">
                    {Array.from({ length: 15 }, (_, i) => 7 + i).map(h => (
                      <SelectItem key={h} value={`${h.toString().padStart(2, '0')}:00`}>
                        {h.toString().padStart(2, '0')}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-text">{t('duration')}</Label>
                <Select value={formData.duration_minutes.toString()} onValueChange={(v) => setFormData({...formData, duration_minutes: parseInt(v)})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 z-[100]">
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                    <SelectItem value="120">120 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Von' : 'From'} *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="bg-slate-50 border-slate-200 text-slate-900"
                  data-testid="recurring-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Bis' : 'To'} *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  className="bg-slate-50 border-slate-200 text-slate-900"
                  data-testid="recurring-end-date"
                />
              </div>
            </div>

            {/* Customer */}
            <div className="space-y-2">
              <Label className="label-text">{language === 'de' ? 'Kunde (optional)' : 'Customer (optional)'}</Label>
              <div className="relative">
                <Input
                  value={formData.customer_id ? selectedCustName : custSearch}
                  onChange={(e) => {
                    if (formData.customer_id) { setFormData({...formData, customer_id: ''}); setSelectedCustName(''); }
                    setCustSearch(e.target.value);
                    if (e.target.value.length < 2) setShowCustDropdown(false);
                  }}
                  onFocus={() => { if (custResults.length > 0) setShowCustDropdown(true); }}
                  className={`bg-slate-50 border-slate-200 ${formData.customer_id ? 'text-emerald-700 font-semibold bg-emerald-50 border-emerald-300' : ''}`}
                  placeholder={language === 'de' ? 'Name, E-Mail oder Telefon suchen...' : 'Search by name, email or phone...'}
                />
                {formData.customer_id && (
                  <button onClick={() => { setFormData({...formData, customer_id: ''}); setSelectedCustName(''); setCustSearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={16} /></button>
                )}
                {showCustDropdown && custResults.length > 0 && !formData.customer_id && (
                  <div className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {custResults.map(u => (
                      <button key={u.id} onClick={() => {
                        setFormData({...formData, customer_id: u.id});
                        setSelectedCustName(`${u.name} (${u.email})`);
                        setCustSearch(''); setShowCustDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm transition-colors border-b border-slate-50 last:border-0">
                        <span className="font-medium text-slate-800">{u.name}</span>
                        <span className="text-slate-400 ml-2 text-xs">{u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="label-text">{language === 'de' ? 'Notizen' : 'Notes'}</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-slate-50 border-slate-200 text-slate-900"
                placeholder={language === 'de' ? 'z.B. Training Gruppe A' : 'e.g. Training Group A'}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowModal(false)}
                className="flex-1 btn-secondary"
              >
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={multiCourt ? selectedCourts.length === 0 || !formData.start_date || !formData.end_date : !formData.court_id || !formData.start_date || !formData.end_date}
                className="flex-1 btn-primary"
                data-testid="submit-recurring"
              >
                {multiCourt && selectedCourts.length > 1
                  ? `${selectedCourts.length} ${language === 'de' ? 'Plätze erstellen' : 'Courts Create'}`
                  : (language === 'de' ? 'Erstellen' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Skip Date Modal */}
      <Dialog open={showSkipModal} onOpenChange={setShowSkipModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="skip-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {language === 'de' ? 'Termin überspringen' : 'Skip Date'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {language === 'de' 
                ? 'Wählen Sie ein Datum zum Überspringen'
                : 'Select a date to skip'}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <Calendar
              mode="single"
              selected={skipDate}
              onSelect={setSkipDate}
              locale={dateLocale}
              className="rounded-lg bg-slate-50 border border-slate-200"
            />
            
            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => { setShowSkipModal(false); setSkipDate(null); }}
                className="flex-1 btn-secondary"
              >
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleSkipDate}
                disabled={!skipDate}
                className="flex-1 btn-primary"
                data-testid="confirm-skip"
              >
                {language === 'de' ? 'Überspringen' : 'Skip'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{language === 'de' ? 'Bestätigen' : 'Confirm'}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 mt-2">{confirmAction?.message}</p>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 border-slate-200 text-slate-600">{language === 'de' ? 'Abbrechen' : 'Cancel'}</Button>
            <Button onClick={() => confirmAction?.onConfirm()} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold">
              {language === 'de' ? 'Löschen' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRecurring;
